import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ExternalLink, Settings, Plus, Trash2 } from 'lucide-react';
import Link from 'next/link';
import OpenAPISelector from './OpenAPISelector';
import DeployModal from './DeployModal'; 

interface DeploymentStatus {
  [key: string]: {
    isBuilding: boolean;
    isDeployed: boolean;
    hasError: boolean;
    apiDevPortal?: string;
    MIAproject?: string;
    apiVersion?: string;
  };
}

interface DeployedProject {
  id: string;
  MIAproject: string;
  MIAprojectShort: string;
  projectName: string;
  path: string;
  apiVersion: string;
  deployedAt: string;
  lastUpdated: string;
  status: 'success' | 'deployed' | 'building' | 'deleting' | 'error';
  source: 'github' | 'thirdparty' | 'local';
  githubUrl?: string;
  apiDevPortal?: string;
}

interface ProjectSelection {
  projectName: string;
  path: string;
  apiVersion: string;
  MIAproject: string;
  githubUrl?: string;
  source: 'github' | 'thirdparty' | 'local';
}

interface DeployData {
  projectName: string;
  path: string;
  branchName: string;
  installationId: string;
  githubAppName: string;
  githubAppId: string;
}

interface DeployedProjectTableProps {
  username: string;
  githubHandle: string;
  defaultSettings?: any;
  branchName: string;
  installationId: string;
  githubAppName: string;
  githubAppId: string;
}


const DeployedProjectTable: React.FC<DeployedProjectTableProps> = ({
  username,
  githubHandle,
  defaultSettings,
  branchName,
  installationId,
  githubAppName,
  githubAppId
}) => {
  // Immediate debug logging
/*  console.log('=== DeployedProjectTable COMPONENT START ===');
  console.log('Props received:', {
    username,
    githubHandle,
    branchName,
    installationId,
    githubAppName,
    githubAppId,
    hasDefaultSettings: !!defaultSettings
  });
*/
  const [deployedProjects, setDeployedProjects] = useState<DeployedProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus>({});
  const [selectedProject, setSelectedProject] = useState<DeployedProject | null>(null);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [deployData, setDeployData] = useState<DeployData | null>(null);
  const { toast } = useToast();

  // Fetch deployed projects
  const fetchDeployedProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/DeployedProjects', {
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch deployed projects: ${response.status}`);
      }

      const data = await response.json();
      const projects = data.deployedProjects || [];
      
      setDeployedProjects(projects);
    } catch (err) {
      console.error('Error fetching deployed projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch deployed projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    console.log('🔄 INITIAL LOAD useEffect triggered - username:', username, 'githubHandle:', githubHandle);
    if (username) {
      console.log('✅ Username available, calling fetchDeployedProjects');
      fetchDeployedProjects();
    } else {
      console.log('❌ No username available, skipping fetch');
    }
  }, [username, fetchDeployedProjects]);

  // Simple polling interval - ts.txt updates indicate when to refresh
  useEffect(() => {
    if (!githubHandle) {
      return;
    }

    let lastKnownTimestamp: string | null = null;

    const checkForUpdates = async () => {
      try {
        const response = await fetch(
          `https://storage.myinstantapi.com/users/${githubHandle}/public/ts.txt`,
          { cache: 'no-store' }
        );

        if (response.ok) {
          const timestamp = await response.text().then(t => t.trim());
          
          if (lastKnownTimestamp === null) {
            // First check - just store the timestamp
            lastKnownTimestamp = timestamp;
          } else if (lastKnownTimestamp !== timestamp) {
            // Timestamp changed - refresh projects
            lastKnownTimestamp = timestamp;
            fetchDeployedProjects();
          }
        }
      } catch (error) {
        // Silently handle polling errors
      }
    };

    // Check immediately and then every 5 seconds
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 5000);

    return () => clearInterval(interval);
  }, [githubHandle, fetchDeployedProjects]);

  const handleShowManage = useCallback((project: DeployedProject) => {
    setSelectedProject(project);
    setIsManageOpen(true);
  }, []);

  const handleOpenConfig = useCallback((project: DeployedProject) => {
    setDeployData({
      projectName: project.projectName,
      path: project.path,
      branchName,
      installationId,
      githubAppName,
      githubAppId
    });
    setIsDeployModalOpen(true);
  }, [branchName, installationId, githubAppName, githubAppId]);

  const handleDelete = async (project: DeployedProject): Promise<void> => {
    setIsDeleting(true);
    
    try {
      const params = {
        projectName: project.projectName,
        path: project.path.replace(/[^a-zA-Z0-9]/g, '_'),
        username: githubHandle,
        installationId,
        branchName,
        githubAppName,
        githubAppId,
        MIAproject: project.MIAproject,
        apiVersion: project.apiVersion
      };
      
      const response = await fetch(`/api/api_setup?${new URLSearchParams(params)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete API deployment');
      }

      // Remove from local state
      setDeployedProjects(prev => prev.filter(p => p.id !== project.id));

      toast({
        title: "Success",
        description: "API deployment deleted successfully",
      });
      setIsManageOpen(false);

    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete API deployment",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isEmpty = deployedProjects.length === 0 && !isLoading;

  const getStatusDisplay = (project: DeployedProject) => {
    switch (project.status) {
      case 'building':
        return (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            <span className="text-sm text-blue-600">Building...</span>
          </>
        );
      case 'deleting':
        return (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-red-500" />
            <span className="text-sm text-red-600">Deleting...</span>
          </>
        );
      case 'deployed':
      case 'success':
        return (
          <>
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-green-600">Deployed</span>
          </>
        );
      case 'error':
      default:
        return (
          <>
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-sm text-red-600">Error</span>
          </>
        );
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEmpty ? 'Deploy Your First API' : 'Your Hosted APIs'}
        </h2>
        {!isEmpty && (
        <Button
          onClick={() => setIsSelectorOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add New API
        </Button>)}
      </div>

      {isEmpty ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="space-y-4">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <Plus className="h-6 w-6 text-gray-500" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">No deployed APIs yet</h3>
              <p className="text-gray-500 mt-1">
                Deploy your first API from GitHub repositories, third-party sources, or local files
              </p>
            </div>
            <Button
              onClick={() => setIsSelectorOpen(true)}
              className="flex items-center gap-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              Deploy Your First API
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[65%] py-4 text-sm font-semibold text-gray-900">
                  API Project
                </TableHead>
                <TableHead className="w-[15%] py-4 text-sm font-semibold text-gray-900">
                  Version
                </TableHead>
                <TableHead className="w-[15%] py-4 text-sm font-semibold text-gray-900">
                  Status
                </TableHead>
                <TableHead className="w-[5%] py-4 text-sm font-semibold text-gray-900 text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && deployedProjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin mr-2" />
                      <span>Loading deployed APIs...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                deployedProjects.map((project) => {
                  const isDeployed = project.status === 'deployed' || project.status === 'success';
                  const isBuilding = project.status === 'building' || project.status === 'deleting';
                  
                  return (
                    <TableRow
                      key={project.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <TableCell className="py-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900">{project.MIAprojectShort}</span>
                          <span className="text-sm text-gray-500">
                            {project.githubUrl ? (
                              <Link
                                href={project.githubUrl}
                                className="hover:underline text-blue-600 flex items-center gap-1"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {project.projectName}
                                <span>{" > "}</span>
                                {project.path}
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            ) : (
                              project.path
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          v{project.apiVersion}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          {getStatusDisplay(project)}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex justify-center gap-2">
                          {/* Button 1: Main Action */}
                          <Button
                            onClick={() => handleOpenConfig(project)}
                            disabled={isBuilding}
                            variant={isDeployed ? "secondary" : "default"}
                            size="sm"
                            className="min-w-[80px]"
                          >
                            {isBuilding ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                Building
                              </>
                            ) : isDeployed ? (
                              <>
                                <Settings className="h-4 w-4 mr-1" />
                                Update
                              </>
                            ) : (
                              <>
                                <Plus className="h-4 w-4 mr-1" />
                                Deploy
                              </>
                            )}
                          </Button>
                          
                          {/* Button 2: Settings (for deployed APIs) */}
                          {isDeployed && (
                            <Button
                              onClick={() => handleShowManage(project)}
                              variant="outline"
                              size="sm"
                              title="Delete API"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                          
                          {/* Button 3: Portal (only if has portal) */}
                          {isDeployed && project.apiDevPortal && (
                            <a
                              href={`https://apiportal.myInstantAPI.com/?project=${project.MIAprojectShort}&apiVersion=${project.apiVersion}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                              title="Open API Portal"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* OpenAPI Selector Modal */}
      <OpenAPISelector
        isOpen={isSelectorOpen}
        onOpenChange={setIsSelectorOpen}
        onProjectSelected={(selection: ProjectSelection) => {
          setDeployData({
            projectName: selection.projectName,
            path: selection.path,
            branchName,
            installationId,
            githubAppName,
            githubAppId
          });
          setIsSelectorOpen(false);
          setIsDeployModalOpen(true);
        }}
        githubHandle={githubHandle}
        branchName={branchName}
        installationId={installationId}
        githubAppName={githubAppName}
        githubAppId={githubAppId}
      />

      {/* Deploy Modal */}
      {deployData && (
        <DeployModal
          isOpen={isDeployModalOpen}
          onOpenChange={(open) => {
            setIsDeployModalOpen(open);
            if (!open) setDeployData(null);
          }}
          deployData={deployData}
          onSuccess={() => {
            // Refresh the projects list
            fetchDeployedProjects();
            setIsDeployModalOpen(false);
            setDeployData(null);
          }}
        />
      )}

      {/* Manage Project Modal */}
      <Dialog open={isManageOpen} onOpenChange={setIsManageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manage API - {selectedProject?.projectName}
            </DialogTitle>
            <DialogDescription>
              {selectedProject?.path} • v{selectedProject?.apiVersion}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* API Portal Button */}
            {selectedProject?.apiDevPortal && (
              <a
                href={selectedProject.apiDevPortal}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 w-full"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open API Portal
              </a>
            )}
            
            {/* API Endpoint Info */}
            {selectedProject && (
              <div className="p-3 bg-gray-50 rounded-md">
                <div className="text-sm text-gray-600 mb-1">API Endpoint</div>
                <div className="text-sm font-mono break-all">
                  {selectedProject.githubUrl ? 
                    `https://${selectedProject.projectName}.myInstantAPI.com/prod/${selectedProject.apiVersion}` :
                    'API endpoint not available'
                  }
                </div>
              </div>
            )}
            
            {/* Danger Zone */}
            <div className="border-t pt-4">
              <div className="text-sm font-medium text-red-600 mb-2">Danger Zone</div>
              <p className="text-sm text-gray-600 mb-3">
                This will permanently delete the API deployment. This action cannot be undone.
              </p>
              <Button
                variant="destructive"
                size="lg"
                className="w-full"
                disabled={isDeleting}
                onClick={() => selectedProject && handleDelete(selectedProject)}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Settings className="h-4 w-4 mr-2" />
                    Delete API Deployment
                  </>
                )}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsManageOpen(false)}
              disabled={isDeleting}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeployedProjectTable;